/**
 * RowActions — the actions for one record (call, WhatsApp, send email, edit, delete …).
 *
 * Two looks, same `actions` list:
 *   - icons   a row of small icon buttons (table rows on desktop; inside a <DataTable> they
 *             appear on row hover with a mouse and are always visible on touch — DataTable.css)
 *   - menu    one "⋯" button opening a labelled menu (phones, where a row of icons doesn't fit,
 *             and drawer headers)
 * By default the look follows the screen: icons from the md breakpoint up, menu below it.
 *
 * Props:
 *   actions  array of {
 *     key      string   unique key
 *     icon     node     Ant icon
 *     label    string   tooltip / menu text (+ aria-label)
 *     onClick  fn       click handler (event is stopped so row clicks don't fire)
 *     href     string   render as a link instead (e.g. "tel:0501234567")
 *     danger   bool     red (delete)
 *     color    string   brand color for the icon (e.g. WhatsApp green when it's usable)
 *     muted    bool     faded look: "not active right now" (e.g. WhatsApp disconnected).
 *                       Still clickable — the label should explain the state.
 *     hidden   bool     skip this action (e.g. WhatsApp when the phone is invalid)
 *   }
 *   mode     'auto' (default) | 'icons' | 'menu'
 *
 * Usage:
 *   <RowActions actions={[
 *     { key: 'edit', icon: <EditOutlined />, label: 'עריכה', onClick: () => open(row) },
 *     { key: 'delete', icon: <DeleteOutlined />, label: 'מחיקה', danger: true, onClick: () => remove(row) },
 *   ]} />
 */
import React from 'react';
import { Button, Tooltip, Dropdown, Grid } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import styles from './RowActions.css';

const RowActions = ({ actions, mode = 'auto' }) => {
  const screens = Grid.useBreakpoint();
  const visible = actions.filter((a) => !a.hidden);
  const asMenu = mode === 'menu' || (mode === 'auto' && screens.md === false);

  if (asMenu) {
    const items = visible.map((a) => ({
      key: a.key,
      danger: a.danger,
      icon: (
        <span style={a.color && !a.muted ? { color: a.color } : undefined} className={a.muted ? styles.muted : undefined}>
          {a.icon}
        </span>
      ),
      label: a.href ? <a href={a.href}>{a.label}</a> : a.label,
    }));
    return (
      <span className={`ui-row-actions ${styles.actions}`} onClick={(e) => e.stopPropagation()}>
        <Dropdown
          trigger={['click']}
          placement="bottomLeft"
          menu={{
            items,
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              const a = visible.find((x) => x.key === key);
              if (a && a.onClick) a.onClick(domEvent);
            },
          }}
        >
          {/* Big, visible tap target (40px) — this is the only way to the actions on a phone. */}
          <Button type="text" icon={<MoreOutlined />} aria-label="פעולות" className={styles.menuTrigger} />
        </Dropdown>
      </span>
    );
  }

  return (
    <div className={`ui-row-actions ${styles.actions}`}>
      {visible.map((a) => (
        <Tooltip key={a.key} title={a.label} mouseEnterDelay={0.4}>
          <Button
            type="text"
            size="small"
            icon={a.icon}
            href={a.href}
            aria-label={a.label}
            className={[styles.button, a.danger ? styles.danger : '', a.muted ? styles.muted : ''].join(' ')}
            style={a.color && !a.muted ? { color: a.color } : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (a.onClick) a.onClick(e);
            }}
          />
        </Tooltip>
      ))}
    </div>
  );
};

export default RowActions;
